import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Clock, AlertTriangle, TrendingDown, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { logger } from '../lib/logger';

/**
 * TimeInStageCard — mostra il tempo medio/mediano di permanenza per ogni
 * stage della pipeline + top N lead più fermi in ogni stage.
 *
 * Usa /api/kpis/time-in-stage. Rispetta il filtro date + date_mode passato
 * dal Dashboard, così i numeri sono coerenti con il resto della pagina.
 *
 * Props:
 *   startDate:  YYYY-MM-DD
 *   endDate:    YYYY-MM-DD
 *   dateMode:   'origin' | 'activity'
 *   agentId:    string | null
 *   refreshTick: number (increment per forzare reload)
 */
export default function TimeInStageCard({
  startDate, endDate, dateMode = 'activity', agentId = null, refreshTick = 0,
}) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStage, setExpandedStage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        date_mode: dateMode,
        top_n: '5',
      });
      if (agentId) params.append('agent_id', agentId);
      const r = await api.get(`/kpis/time-in-stage?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      logger.error('TimeInStageCard: load failed', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, dateMode, agentId]);

  useEffect(() => { load(); }, [load, refreshTick]);

  // Only show stages with active leads → hide zero-count clutter.
  const visibleStages = (data?.stages || []).filter((s) => s.active_count > 0);

  if (loading) {
    return (
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-serif flex items-center gap-2">
            <Clock className="w-4 h-4 text-[var(--gp-gold)]" /> Tempo medio in stage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={`sk-${i}`} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border" data-testid="time-in-stage-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-serif flex items-center gap-2">
            <Clock className="w-4 h-4 text-[var(--gp-gold)]" /> Tempo medio in stage
          </CardTitle>
          <Badge variant="outline" className="text-[10px]">
            {data?.total_leads_in_cohort ?? 0} {t('timeInStage.leadsInPeriod', 'lead nel periodo')}
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          {t('timeInStage.subtitle', 'Quanti giorni un lead resta mediamente fermo in ogni fase. Espandi per vedere i lead più impantanati.')}
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {visibleStages.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {t('timeInStage.empty', 'Nessun lead attivo negli stage per il periodo selezionato.')}
          </div>
        ) : (
          <div className="divide-y">
            {visibleStages.map((s) => {
              const isExpanded = expandedStage === s.key;
              // Colour intensity — over 30 gg = warning, over 60 gg = danger
              const avgTone =
                s.avg_days >= 60 ? 'text-red-600'
                  : s.avg_days >= 30 ? 'text-amber-600'
                    : 'text-emerald-600';
              return (
                <div key={s.key} data-testid={`tis-row-${s.key}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedStage(isExpanded ? null : s.key)}
                    className="w-full grid grid-cols-[minmax(0,1fr)_auto_auto_auto_16px] items-center gap-3 px-4 py-2.5 hover:bg-[var(--surface-2)] transition-colors text-left"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate text-[hsl(var(--primary))]">
                        {s.label}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {s.active_count} lead attivi
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${avgTone}`} data-testid={`tis-avg-${s.key}`}>
                        {s.avg_days} gg
                      </div>
                      <div className="text-[10px] text-muted-foreground">media</div>
                    </div>
                    <div className="text-right hidden md:block">
                      <div className="text-sm font-medium text-muted-foreground">
                        {s.median_days} gg
                      </div>
                      <div className="text-[10px] text-muted-foreground">mediana</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${s.max_days >= 60 ? 'text-red-600' : s.max_days >= 30 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                        {s.max_days} gg
                      </div>
                      <div className="text-[10px] text-muted-foreground">max</div>
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  </button>

                  {isExpanded && s.top_stuck?.length > 0 && (
                    <div className="bg-[var(--surface-2)]/60 px-4 py-2 space-y-1.5" data-testid={`tis-details-${s.key}`}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                        {t('timeInStage.topStuck', 'Lead più fermi in questo stage')}
                      </div>
                      {s.top_stuck.map((l) => (
                        <Link
                          key={l.id}
                          to={`/app/leads/${l.id}`}
                          className="flex items-center justify-between gap-2 text-xs px-2 py-1 rounded hover:bg-white transition-colors"
                          data-testid={`tis-stuck-${l.id}`}
                        >
                          <span className="font-medium truncate">{l.name}</span>
                          <span className={`flex items-center gap-1 flex-shrink-0 ${l.days >= 60 ? 'text-red-600' : l.days >= 30 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                            <TrendingDown className="w-3 h-3" />
                            {l.days} gg
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
